import React, { useState } from 'react';
import { ArrowRight, Mail, Phone, Clock, CheckCircle, MessageSquare, Calendar, FileText, X, Upload } from 'lucide-react';
import Layout from '../layouts/Layout';
import apiClient from '../helpers/apiClient';
import NotificationPopup from '../components/NotificationPopup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';
import projectTypes from '../data/projectTypes';

const ContactPage = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        budget: '',
        timeline: '',
        projectType: '',
        message: ''
    });

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // Notification state
    const [notification, setNotification] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Notification functions
    const showNotification = (message, type = 'success', duration = 5000) => {
        setNotification({ message, type });

        // Auto-hide after duration
        setTimeout(() => {
            setNotification(null);
        }, duration);
    };

    const hideNotification = () => {
        setNotification(null);
    };

    const validateFile = (file) => {
        const maxSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'image/jpeg',
            'image/jpg',
            'image/png',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ];

        if (file.size > maxSize) {
            return `File "${file.name}" is too large. Maximum size is 10MB.`;
        }

        if (!allowedTypes.includes(file.type)) {
            return `File type "${file.type}" is not allowed. Please use PDF, DOC, TXT, JPG, PNG, or XLS files.`;
        }

        return null;
    };

    const handleFileUpload = (files) => {
        const fileArray = Array.from(files);
        const validFiles = [];
        const errors = [];

        fileArray.forEach(file => {
            const error = validateFile(file);
            if (error) {
                errors.push(error);
            } else {
                // Check if file already exists
                const exists = uploadedFiles.some(existingFile =>
                    existingFile.name === file.name && existingFile.size === file.size
                );

                if (!exists) {
                    validFiles.push({
                        file: file,
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        id: Date.now() + Math.random() // Unique ID for deletion
                    });
                }
            }
        });

        if (errors.length > 0) {
            showNotification(errors.join('. '), 'error', 8000);
        }

        if (validFiles.length > 0) {
            setUploadedFiles(prev => [...prev, ...validFiles]);
            showNotification(`${validFiles.length} file(s) added successfully`, 'success', 3000);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileUpload(e.target.files);
            // Reset input so same file can be selected again if needed
            e.target.value = '';
        }
    };

    const removeFile = (fileId) => {
        const fileToRemove = uploadedFiles.find(file => file.id === fileId);
        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
        if (fileToRemove) {
            showNotification(`"${fileToRemove.name}" removed`, 'info', 2000);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileUpload(e.dataTransfer.files);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }

        try {
            // Execute reCAPTCHA
            const token = await executeRecaptcha('contact_form');

            // Create FormData object for file upload
            const submitData = new FormData();

            // Add form fields
            Object.keys(formData).forEach(key => {
                submitData.append(key, formData[key]);
            });

            // Add reCAPTCHA token
            submitData.append('recaptchaToken', token);

            // Add files - Use the correct field name that matches PHP expectation
            uploadedFiles.forEach((fileObj) => {
                submitData.append('documents[]', fileObj.file);
            });

            const response = await apiClient.submitContactForm(submitData);

            if (response.success) {
                // Success notification
                showNotification(
                    `${response.message}.`,
                    'success',
                    6000
                );

                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    phone: '',
                    budget: '',
                    timeline: '',
                    projectType: '',
                    message: ''
                });
                setUploadedFiles([]);
                setAgreedToTerms(false);
            } else {
                throw new Error(response.error || 'Form submission failed');
            }

        } catch (error) {
            console.error('Submit error:', error);
            showNotification(
                `Error: ${error.message}`,
                'error',
                8000
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactMethods = [
        {
            icon: <Mail className="w-6 h-6" />,
            title: "Email",
            value: "hello@kgcinnovations.com",
            description: "Send us a detailed message",
            action: "mailto:hello@kgcinnovations.com"
        },
        {
            icon: <Phone className="w-6 h-6" />,
            title: "Phone",
            value: "+44 (0) 20 7046 0289",
            description: "Speak directly with our team",
            action: "tel:+442070460289"
        },
        // {
        //     icon: <MessageSquare className="w-6 h-6" />,
        //     title: "WhatsApp",
        //     value: "+44 7123 456789",
        //     description: "Quick questions and updates",
        //     action: "https://wa.me/447123456789"
        // },
        {
            icon: <Calendar className="w-6 h-6" />,
            title: "Schedule Call",
            value: "Book a consultation",
            description: "30-minute strategy session",
            action: "/schedule-consultation"
        }
    ];

    const budgetRanges = [
        "Under £500",
        "£500 - £1,500",
        "£1,500 - £3,000",
        "£3,000 - £7,500",
        "£7,500 - £15,000",
        "£15,000 - £30,000",
        "£30,000+",
        "I'm not sure yet"
    ];

    const timelines = [
        "ASAP (Rush project)",
        "1-2 months",
        "3-6 months",
        "6-12 months",
        "12+ months",
        "Flexible"
    ];

    return (
        <Layout
            title="Contact Us"
            description="Get in touch with KGC Innovations Ltd for your web and mobile development needs. UK-based team ready to discuss your project requirements and provide expert solutions."
            keywords="contact web developer UK, hire React developer, web development quote UK, enterprise development consultation, mobile app development contact"
            path="/contact"
        >
            <div className="min-h-screen bg-white">
                {/* Notification Popup */}
                <NotificationPopup notification={notification} hideNotification={hideNotification} />

                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto text-center">
                        <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                            Let's Build Something
                            <span className="block text-slate-700 mt-2">Amazing Together</span>
                        </h1>

                        <p className="text-xl text-slate-600 mb-12 leading-relaxed max-w-4xl mx-auto">
                            Ready to transform your business with cutting-edge technology? We're here to discuss your project,
                            understand your goals, and propose the perfect solution for your needs.
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8 border-t border-slate-200">
                            <div>
                                <div className="text-3xl font-bold text-slate-900">24hr</div>
                                <div className="text-slate-600">Response Time</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">Free</div>
                                <div className="text-slate-600">Consultation</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">UK</div>
                                <div className="text-slate-600">Based Team</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">10+</div>
                                <div className="text-slate-600">Years Experience</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Contact Methods */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Get In Touch</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Choose the method that works best for you
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                            {contactMethods.map((method, index) => (
                                <a
                                    key={index}
                                    href={method.action}
                                    className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-all hover:-translate-y-1 group"
                                >
                                    <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                                        {method.icon}
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">{method.title}</h3>
                                    <div className="font-semibold text-slate-900 mb-2">{method.value}</div>
                                    <p className="text-slate-600 text-sm">{method.description}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact Form */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-4xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Start Your Project</h2>
                            <p className="text-xl text-slate-600">
                                Tell us about your project and we'll provide a detailed proposal within 24 hours
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                {/* Personal Information */}
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-6">Personal Information</h3>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-slate-900 font-medium mb-2" htmlFor="name">
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                placeholder="Your full name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-slate-900 font-medium mb-2" htmlFor="email">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                placeholder="your@company.com"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-slate-900 font-medium mb-2" htmlFor="company">
                                                Company Name
                                            </label>
                                            <input
                                                type="text"
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                placeholder="Your company name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-slate-900 font-medium mb-2" htmlFor="phone">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                                placeholder="+44 7123 456789"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Project Information */}
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-6">Project Information</h3>
                                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                                        <div>
                                            <label className="block text-slate-900 font-medium mb-2" htmlFor="projectType">
                                                Project Type *
                                            </label>
                                            <select
                                                id="projectType"
                                                name="projectType"
                                                value={formData.projectType}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                            >
                                                <option value="">Select project type</option>
                                                {projectTypes.map((type, index) => (
                                                    <option key={index} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-slate-900 font-medium mb-2" htmlFor="budget">
                                                Project Budget
                                            </label>
                                            <select
                                                id="budget"
                                                name="budget"
                                                value={formData.budget}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                            >
                                                <option value="">Select budget range</option>
                                                {budgetRanges.map((range, index) => (
                                                    <option key={index} value={range}>{range}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-slate-900 font-medium mb-2" htmlFor="timeline">
                                                Timeline
                                            </label>
                                            <select
                                                id="timeline"
                                                name="timeline"
                                                value={formData.timeline}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors"
                                            >
                                                <option value="">Select timeline</option>
                                                {timelines.map((time, index) => (
                                                    <option key={index} value={time}>{time}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-slate-900 font-medium mb-2" htmlFor="message">
                                            Project Details *
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            rows={6}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-colors resize-none"
                                            placeholder="Please describe your project requirements, goals, target audience, and any specific features you need. The more details you provide, the better we can help you."
                                        />
                                    </div>
                                </div>

                                {/* File Upload */}
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-6">Supporting Documents</h3>

                                    {/* Upload Area */}
                                    <div
                                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                                            ? 'border-slate-900 bg-slate-50'
                                            : 'border-slate-300 hover:border-slate-400'
                                            }`}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            type="file"
                                            id="documents"
                                            name="documents"
                                            multiple
                                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx"
                                            onChange={handleFileInputChange}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor="documents"
                                            className="cursor-pointer flex flex-col items-center"
                                        >
                                            <Upload className="w-12 h-12 text-slate-400 mb-4" />
                                            <span className="text-slate-700 font-medium text-lg mb-2">Upload Project Files</span>
                                            <span className="text-slate-500 mb-2">
                                                Drag and drop files here, or click to select
                                            </span>
                                            <span className="text-slate-400 text-sm">
                                                Accepted: PDF, DOC, TXT, JPG, PNG, XLS (max 10MB each)
                                            </span>
                                        </label>
                                    </div>

                                    {/* Uploaded Files List */}
                                    {uploadedFiles.length > 0 && (
                                        <div className="mt-6">
                                            <div className="text-sm font-medium text-slate-900 mb-4">
                                                Uploaded files ({uploadedFiles.length}):
                                            </div>
                                            <div className="space-y-3">
                                                {uploadedFiles.map((fileObj) => (
                                                    <div
                                                        key={fileObj.id}
                                                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                                                    >
                                                        <div className="flex items-center flex-1 min-w-0">
                                                            <FileText className="w-5 h-5 text-slate-500 mr-3 flex-shrink-0" />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="text-sm font-medium text-slate-900 truncate">
                                                                    {fileObj.name}
                                                                </div>
                                                                <div className="text-xs text-slate-500">
                                                                    {formatFileSize(fileObj.size)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeFile(fileObj.id)}
                                                            className="ml-4 p-1 text-slate-400 hover:text-red-500 transition-colors"
                                                            title="Remove file"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Terms Agreement & Submit */}
                                <div className="pt-6 border-t border-slate-200">
                                    {/* Terms Agreement Checkbox */}
                                    <div className="mb-6">
                                        <label className="flex items-start space-x-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={agreedToTerms}
                                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                                required
                                                className="mt-1 w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900 focus:ring-2"
                                            />
                                            <span className="text-sm text-slate-600 leading-relaxed">
                                                I agree to the{' '}
                                                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
                                                    Privacy Policy
                                                </Link>{' '}
                                                and{' '}
                                                <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
                                                    Terms of Service
                                                </Link>
                                                . This site is protected by reCAPTCHA and the Google{' '}
                                                <Link to="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
                                                    Privacy Policy
                                                </Link>{' '}
                                                and{' '}
                                                <Link to="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline hover:no-underline">
                                                    Terms of Service
                                                </Link>{' '}
                                                apply. *
                                            </span>
                                        </label>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={!executeRecaptcha || isSubmitting || !agreedToTerms}
                                        className="w-full bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <span>Sending Message...</span>
                                        ) : (
                                            <>
                                                Send Message <ArrowRight className="ml-2 w-5 h-5" />
                                            </>
                                        )}
                                    </button>

                                    <p className="text-sm text-slate-500 text-center mt-4">
                                        We typically respond within 24 hours with a detailed project proposal and next steps.
                                    </p>
                                </div>
                            </form>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-4xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
                            <p className="text-xl text-slate-600">
                                Quick answers to common questions about working with us
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    question: "How quickly can you start my project?",
                                    answer: "We can typically start new projects within 1-2 weeks of contract signing, depending on our current workload and project complexity."
                                },
                                {
                                    question: "Do you provide ongoing support?",
                                    answer: "Yes, we offer comprehensive maintenance and support packages to ensure your application continues to perform optimally after launch."
                                },
                                {
                                    question: "What's your development process?",
                                    answer: "We follow an agile methodology with regular sprints, progress updates, and client feedback sessions to ensure transparency and quality."
                                },
                                {
                                    question: "Can you work with our existing team?",
                                    answer: "Absolutely! We often work as an extension of our clients' teams and can integrate seamlessly with your existing development processes."
                                },
                                {
                                    question: "Do you sign NDAs?",
                                    answer: "Yes, we're happy to sign non-disclosure agreements to protect your confidential information and business ideas."
                                },
                                {
                                    question: "What if I need changes after launch?",
                                    answer: "We provide post-launch support and can implement changes, updates, and new features as your business grows and evolves."
                                }
                            ].map((faq, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                                    <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Next Steps */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">What Happens Next?</h2>
                            <p className="text-xl text-slate-600">
                                Our proven process ensures a smooth start to your project
                            </p>
                        </div>

                        <div className="grid md:grid-cols-4 gap-8">
                            {[
                                {
                                    step: "01",
                                    title: "Initial Response",
                                    description: "We'll respond to your enquiry within 24 hours to schedule a consultation call.",
                                    icon: <Clock className="w-6 h-6" />
                                },
                                {
                                    step: "02",
                                    title: "Strategy Session",
                                    description: "30-60 minute call to discuss your requirements, goals, and technical approach.",
                                    icon: <MessageSquare className="w-6 h-6" />
                                },
                                {
                                    step: "03",
                                    title: "Detailed Proposal",
                                    description: "Comprehensive proposal with timeline, costs, and technical specifications.",
                                    icon: <FileText className="w-6 h-6" />
                                },
                                {
                                    step: "04",
                                    title: "Project Kickoff",
                                    description: "Contract signing, team introductions, and project kickoff meeting.",
                                    icon: <CheckCircle className="w-6 h-6" />
                                }
                            ].map((step, index) => (
                                <div key={index} className="text-center">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mx-auto mb-6">
                                        {step.icon}
                                    </div>
                                    <div className="text-3xl font-bold text-slate-300 mb-2">{step.step}</div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{step.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{step.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default ContactPage;